import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import PointOfSaleIcon from '@mui/icons-material/PointOfSaleOutlined';
import PrintIcon from '@mui/icons-material/PrintOutlined';
import ReceiptIcon from '@mui/icons-material/ReceiptOutlined';
import ReplayIcon from '@mui/icons-material/ReplayOutlined';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import SendIcon from '@mui/icons-material/SendOutlined';

import ordersApi from '../../../api/ordersApi';
import documentLookupApi from '../../../api/documentLookupApi';
import { getErrorMessage, getBlobErrorMessage } from '../../../utils/errorHandler';
import { orderStatusLabel } from '../../../utils/orderStatuses';
import { printBlob, triggerBlobDownload } from '../../../utils/downloadFile';
import { paymentMethodLabel } from '../../../utils/paymentMethods';
import { googleMapsLink, shippingMethodLabel } from '../../../utils/shippingMethods';
import { INVOICE_STATUS_META, isAcceptedInvoiceStatus as isAcceptedStatus } from '../../../utils/invoiceStatus';
import VoidInvoiceDialog from './VoidInvoiceDialog';

const formatMoney = (value, currency) =>
  new Intl.NumberFormat(currency === 'USD' ? 'en-US' : 'es-PE', { style: 'currency', currency }).format(value || 0);

export default function OrderDetailDialog({ open, onClose, order, statuses, onChanged }) {
  const { enqueueSnackbar } = useSnackbar();
  const [statusId, setStatusId] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);
  // Tipo de documento del cliente para el comprobante: RUC exige factura; DNI, C.E.
  // (carne de extranjeria) o pasaporte van con boleta. Solo DNI/RUC tienen busqueda
  // automatica (ver handleLookupDocument): Decolecta no cubre C.E. ni pasaporte.
  const [documentType, setDocumentType] = useState('DNI');
  const [documentNumber, setDocumentNumber] = useState('');
  // Razon social (factura) o nombre (boleta) del cliente: a mano o autocompletada
  // consultando el RUC/DNI de arriba (ver handleLookupDocument).
  const [documentName, setDocumentName] = useState('');
  // Direccion del cliente para la factura (obligatoria ante SUNAT): a mano o autocompletada
  // consultando el RUC/DNI de arriba (ver handleLookupDocument).
  const [documentAddress, setDocumentAddress] = useState('');
  const [lookingUpDocument, setLookingUpDocument] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [voiding, setVoiding] = useState(false);
  // Descarga del comprobante: A4 (PDF normal) o ticket de 80mm para tiketera termica
  // (ej. Fukun FK-POS80-BF), cada una con su propio estado de carga.
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingTicket, setDownloadingTicket] = useState(false);
  const [printingTicket, setPrintingTicket] = useState(null);
  const [emailTo, setEmailTo] = useState('');
  const [sending, setSending] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [resending, setResending] = useState(false);
  const [downloadingNota, setDownloadingNota] = useState(null);
  // Nombre que se imprime en la nota de venta (solo el nombre, sin DNI): se guarda en el pedido
  // al generarla, si cambio (ver saveNoteName).
  const [noteName, setNoteName] = useState('');
  const [savedNoteName, setSavedNoteName] = useState('');
  const [downloadingCreditNote, setDownloadingCreditNote] = useState(false);
  const [voidDialogOpen, setVoidDialogOpen] = useState(false);
  // Copia local de los items del pedido: se actualiza sola al completar una serie (ver
  // handleAddItemSerial), sin depender de que el padre vuelva a pedir el pedido completo.
  const [items, setItems] = useState([]);
  const [serialInputs, setSerialInputs] = useState({});
  const [addingSerialItemId, setAddingSerialItemId] = useState(null);
  // Inputs de serie por item y a cual devolver el foco tras guardar, para seguir
  // escaneando con la pistola sin tener que hacer clic en el siguiente campo.
  const serialInputRefs = useRef({});
  const [serialFocusItemId, setSerialFocusItemId] = useState(null);

  useEffect(() => {
    if (serialFocusItemId !== null && addingSerialItemId === null) {
      serialInputRefs.current[serialFocusItemId]?.focus();
      setSerialFocusItemId(null);
    }
  }, [serialFocusItemId, addingSerialItemId, items]);

  useEffect(() => {
    if (order) {
      setStatusId(order.status?.id || '');
      setEmailTo(order.client?.email || '');
      setNoteName(order.customer_name || order.client?.name || '');
      setSavedNoteName(order.customer_name || '');
      setDocumentType('DNI');
      setDocumentNumber('');
      setDocumentName('');
      setDocumentAddress('');
      setInvoice(order.invoice || null);
      setItems(order.items || []);
      setSerialInputs({});
    }
  }, [order]);

  if (!order) {
    return null;
  }

  // "Vigente": cualquier comprobante que no haya sido anulado (incluye rechazado/con error,
  // que se resuelven reintentando el envío o anulando para emitir uno nuevo), ver
  // OrderController::storeInvoice.
  const hasActiveInvoice = invoice && invoice.status !== 'anulada';
  const isAccepted = invoice && isAcceptedStatus(invoice.status);
  const canResend = invoice && ['error', 'rechazada'].includes(invoice.status);

  // RUC exige factura; DNI, C.E. o pasaporte van con boleta (ver documentType arriba).
  const invoiceTypeToIssue = documentType === 'RUC' ? 'factura' : 'boleta';
  // La busqueda automatica (Decolecta) solo cubre RUC y DNI.
  const canLookupDocument = documentType === 'RUC' || documentType === 'DNI';

  const cleanedDocumentNumber = documentNumber.trim();
  const cleanedDocumentName = documentName.trim();
  const cleanedDocumentAddress = documentAddress.trim();

  // Que campos le faltan para poder emitir: la factura siempre exige RUC, razon social y
  // direccion (obligatorios ante SUNAT); la boleta no exige nada sin documento (venta de
  // mostrador), pero si se puso un numero (DNI/C.E./pasaporte) tiene que tener su nombre
  // (buscado o escrito a mano, ver mas abajo).
  const missingDocumentFields =
    invoiceTypeToIssue === 'factura'
      ? !cleanedDocumentNumber || !cleanedDocumentName || !cleanedDocumentAddress
      : Boolean(cleanedDocumentNumber) && !cleanedDocumentName;

  // Cuantas series le faltan a un item: aplica a todo producto, sin importar si ya se
  // registraron series para el antes, para poder rastrear la venta (ver
  // OrderController::missingSerialsMessage).
  // Un producto "libre" de venta express (sin producto del catalogo) no lleva series.
  const missingSerialsCount = (item) =>
    item.product ? Math.max(item.quantity - (item.serials?.length || 0), 0) : 0;
  const hasMissingSerials = items.some((item) => missingSerialsCount(item) > 0);

  const handleStatusChange = async (event) => {
    const newStatusId = event.target.value;
    const previousStatusId = statusId;
    setStatusId(newStatusId);
    setSavingStatus(true);
    try {
      const { data } = await ordersApi.updateOrderStatus(order.id, newStatusId);
      // Las series cambian con el estado (se venden al pagar, se liberan al anular).
      setItems(data.data.items || []);
      enqueueSnackbar('Estado actualizado correctamente.', { variant: 'success' });
      onChanged();
    } catch (error) {
      // P. ej. stock insuficiente al marcarlo como cancelado (pagado): el estado no cambia.
      setStatusId(previousStatusId);
      enqueueSnackbar(getErrorMessage(error, 'No se pudo actualizar el estado.'), { variant: 'error' });
    } finally {
      setSavingStatus(false);
    }
  };

  // Consulta el RUC/DNI ingresado (Decolecta) y autocompleta la razon social/nombre y la
  // direccion del cliente, que se mandan junto con el resto al emitir el comprobante (ver
  // handleIssue).
  const handleLookupDocument = async () => {
    const cleaned = documentNumber.trim();

    if (!canLookupDocument) {
      enqueueSnackbar('La búsqueda automática solo está disponible para DNI o RUC.', { variant: 'warning' });
      return;
    }

    const expectedLength = documentType === 'RUC' ? 11 : 8;
    if (cleaned.length !== expectedLength) {
      enqueueSnackbar(`Ingresa un ${documentType} válido (${expectedLength} dígitos) para buscar.`, {
        variant: 'warning',
      });
      return;
    }

    setLookingUpDocument(true);
    try {
      const { data } = await documentLookupApi.lookup(documentType.toLowerCase(), cleaned);
      if (data.data.name) {
        setDocumentName(data.data.name);
      }
      if (data.data.address) {
        setDocumentAddress(data.data.address);
      }
      enqueueSnackbar(
        data.data.name
          ? `Encontrado: ${data.data.name}${data.data.address ? '' : ' (sin dirección registrada, complétala a mano)'}`
          : 'No se encontró información para ese documento.',
        { variant: data.data.name ? 'success' : 'info' }
      );
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo consultar el documento.'), { variant: 'error' });
    } finally {
      setLookingUpDocument(false);
    }
  };

  const handleIssue = async (type) => {
    if (type === 'factura') {
      if (!cleanedDocumentNumber) {
        enqueueSnackbar('Ingresa el RUC para emitir una factura.', { variant: 'warning' });
        return;
      }
      if (!cleanedDocumentName) {
        enqueueSnackbar('Busca o ingresa la razón social para emitir una factura.', { variant: 'warning' });
        return;
      }
      if (!cleanedDocumentAddress) {
        enqueueSnackbar('Busca el RUC para completar la dirección, obligatoria para emitir una factura.', {
          variant: 'warning',
        });
        return;
      }
    } else if (cleanedDocumentNumber && !cleanedDocumentName) {
      enqueueSnackbar('Ingresa el nombre del cliente para el documento indicado.', { variant: 'warning' });
      return;
    }
    setIssuing(true);
    try {
      const { data } = await ordersApi.issueInvoice(order.id, {
        type,
        customer_document_type: documentNumber ? documentType : undefined,
        customer_document_number: documentNumber || undefined,
        customer_name: documentName.trim() || undefined,
        customer_address: documentAddress.trim() || undefined,
      });
      setInvoice(data.data);
      enqueueSnackbar(`${type === 'factura' ? 'Factura' : 'Boleta'} emitida correctamente.`, { variant: 'success' });
      onChanged();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo emitir el comprobante.'), { variant: 'error' });
    } finally {
      setIssuing(false);
    }
  };

  // Completa la serie que falta en una linea, tipeada a mano o leida con una lectora de
  // barras (que "tipea" el codigo y un Enter, por eso el onKeyDown en el input de abajo).
  const handleAddItemSerial = async (item) => {
    const serie = (serialInputs[item.id] || '').trim();
    if (!serie) {
      return;
    }
    setAddingSerialItemId(item.id);
    try {
      const { data } = await ordersApi.assignItemSerial(order.id, item.id, serie);
      const updatedItems = items.map((it) => (it.id === item.id ? { ...it, serials: data.data.serials } : it));
      setItems(updatedItems);
      setSerialInputs((prev) => ({ ...prev, [item.id]: '' }));

      // Si a esta linea aun le faltan series se queda en el mismo campo; si ya se completo,
      // pasa a la siguiente linea que tenga series pendientes.
      const index = updatedItems.findIndex((it) => it.id === item.id);
      const ordered = [...updatedItems.slice(index), ...updatedItems.slice(0, index)];
      const next = ordered.find((it) => missingSerialsCount(it) > 0);
      if (next) setSerialFocusItemId(next.id);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo agregar la serie.'), { variant: 'error' });
      setSerialFocusItemId(item.id);
    } finally {
      setAddingSerialItemId(null);
    }
  };

  // Pide el sustento (Catalogo 09 de SUNAT) y una observacion en VoidInvoiceDialog antes de
  // anular; el backend envia la nota de credito electronica a SUNAT (ver ordersApi.voidInvoice).
  const handleVoid = async (reasonCode, observation) => {
    setVoiding(true);
    try {
      const { data } = await ordersApi.voidInvoice(order.id, reasonCode, observation);
      setInvoice(data.data);
      setVoidDialogOpen(false);
      enqueueSnackbar(data.message || 'Comprobante anulado correctamente.', { variant: 'success' });
      onChanged();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo anular el comprobante.'), { variant: 'error' });
    } finally {
      setVoiding(false);
    }
  };

  const handleDownloadCreditNote = async () => {
    setDownloadingCreditNote(true);
    try {
      const response = await ordersApi.downloadCreditNotePdf(order.id);
      triggerBlobDownload(response.data, `nota-credito-${invoice?.credit_note?.full_number || `pedido-${order.id}`}.pdf`);
    } catch (error) {
      enqueueSnackbar(await getBlobErrorMessage(error, 'No se pudo generar la nota de crédito.'), { variant: 'error' });
    } finally {
      setDownloadingCreditNote(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const { data } = await ordersApi.resendInvoice(order.id);
      setInvoice(data.data);
      const meta = INVOICE_STATUS_META[data.data.status];
      enqueueSnackbar(
        isAcceptedStatus(data.data.status)
          ? 'Comprobante enviado y aceptado por SUNAT.'
          : `SUNAT respondió: ${meta?.label || data.data.status}.`,
        { variant: isAcceptedStatus(data.data.status) ? 'success' : 'warning' }
      );
      onChanged();
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo reenviar el comprobante.'), { variant: 'error' });
    } finally {
      setResending(false);
    }
  };

  const handleDownload = async (paper) => {
    const isTicket = paper === 'ticket';
    const setLoading = isTicket ? setDownloadingTicket : setDownloadingPdf;
    setLoading(true);
    try {
      const response = await ordersApi.downloadInvoicePdf(order.id, isTicket ? 'ticket' : undefined);
      triggerBlobDownload(response.data, `${invoice.type}-${invoice.full_number}${isTicket ? '-ticket' : ''}.pdf`);
    } catch (error) {
      enqueueSnackbar(await getBlobErrorMessage(error, 'No se pudo descargar el comprobante.'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Imprime el documento en formato ticket de 80mm directo en la tiketera (sin descargarlo):
  // `key` identifica que boton muestra "Generando..." ('invoice', 'credit-note', 'nota-venta').
  const handlePrintTicket = async (key, request, fallbackMessage) => {
    setPrintingTicket(key);
    try {
      const response = await request();
      printBlob(response.data);
    } catch (error) {
      enqueueSnackbar(await getBlobErrorMessage(error, fallbackMessage), { variant: 'error' });
    } finally {
      setPrintingTicket(null);
    }
  };

  // Guarda en el pedido el nombre de la nota de venta si se cambio, antes de generarla.
  const saveNoteName = async () => {
    const name = noteName.trim();
    if (name === savedNoteName) {
      return;
    }
    await ordersApi.updateOrder(order.id, { customer_name: name || null });
    setSavedNoteName(name);
    onChanged?.();
  };

  // Nota de venta: documento interno sin validez tributaria (no pasa por SUNAT). Se descarga
  // en A4 o en ticket de 80mm, igual que la boleta/factura (`downloadingNota` = el formato en curso).
  const handleDownloadNotaVenta = async (paper) => {
    setDownloadingNota(paper);
    try {
      await saveNoteName();
      const response = await ordersApi.downloadNotaVentaPdf(order.id, paper === 'ticket' ? 'ticket' : undefined);
      const suffix = paper === 'ticket' ? '-ticket' : '';
      triggerBlobDownload(response.data, `nota-venta-pedido-${order.id}${suffix}.pdf`);
    } catch (error) {
      enqueueSnackbar(await getBlobErrorMessage(error, 'No se pudo generar la nota de venta.'), { variant: 'error' });
    } finally {
      setDownloadingNota(null);
    }
  };

  const handleSendEmail = async () => {
    if (!emailTo) {
      enqueueSnackbar('Ingresa un correo de destino.', { variant: 'warning' });
      return;
    }
    setSending(true);
    try {
      const { data } = await ordersApi.sendInvoiceEmail(order.id, emailTo);
      enqueueSnackbar(data.message || 'Comprobante enviado por correo.', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo enviar el comprobante.'), { variant: 'error' });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Pedido N.° {order.id}</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary">
                Cliente
              </Typography>
              <Typography>{order.client?.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {order.client?.email}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary">
                Moneda / tipo de cambio
              </Typography>
              <Typography>
                {order.currency === 'USD' ? 'Dólares (US$)' : 'Soles (S/)'} · S/ {order.exchange_rate_used.toFixed(4)}{' '}
                por US$ 1.00
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary">
                Método de pago
              </Typography>
              <Typography>{paymentMethodLabel(order.payment_method)}</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary">
                Documento / comprobante solicitado
              </Typography>
              <Typography>
                {order.document_type && order.document_number ? `${order.document_type} ${order.document_number}` : '—'}
                {order.invoice_type ? ` · ${order.invoice_type === 'factura' ? 'Factura' : 'Boleta'}` : ''}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Typography variant="body2" color="text.secondary">
                Entrega
              </Typography>
              <Typography>
                {order.shipping_method === 'delivery'
                  ? `Envío a domicilio · ${[order.shipping_address, order.shipping_district, order.shipping_city]
                      .filter(Boolean)
                      .join(', ') || '—'}`
                  : shippingMethodLabel(order.shipping_method)}
              </Typography>
              {Number.isFinite(order.shipping_latitude) && Number.isFinite(order.shipping_longitude) && (
                <Link
                  variant="body2"
                  href={googleMapsLink(order.shipping_latitude, order.shipping_longitude)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Ver ubicación en Google Maps
                </Link>
              )}
            </Grid>
            {order.shipping_method === 'envio_provincia' && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  Envío a provincia
                </Typography>
                <Typography>
                  {order.shipping_province || '—'} · {order.shipping_agency || '—'} - {order.shipping_agency_destination || '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Embalaje y envío a la agencia: S/ {order.delivery_cost?.toFixed(2)}
                  {order.currency === 'USD' && ' (se cobra aparte, no está incluido en el total en US$)'}
                </Typography>
              </Grid>
            )}
            {order.delivery_type && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  Delivery
                </Typography>
                <Typography>
                  {order.delivery_type === 'express' ? 'Express (mismo día)' : 'Normal (hasta 3 días)'} · S/{' '}
                  {order.delivery_cost?.toFixed(2)}
                  {order.currency === 'USD' && ' (se cobra aparte, no está incluido en el total en US$)'}
                </Typography>
              </Grid>
            )}
          </Grid>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Producto</TableCell>
                <TableCell align="right">Cant.</TableCell>
                <TableCell align="right">P. Unitario</TableCell>
                <TableCell align="right">Subtotal</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => {
                const missing = missingSerialsCount(item);
                return (
                  <TableRow key={item.id}>
                    <TableCell>
                      {item.product?.name || item.description || 'Producto eliminado'}
                      {item.serials?.length > 0 && (
                        <Typography sx={{ display: 'block' }} variant="caption" color="text.secondary">
                          Serie: {item.serials.join(', ')}
                        </Typography>
                      )}
                      {missing > 0 && (
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 0.5 }}>
                          <TextField
                            size="small"
                            variant="standard"
                            placeholder={`Falta${missing > 1 ? `n ${missing} series` : ' serie'}: escanea o escribe`}
                            value={serialInputs[item.id] || ''}
                            onChange={(e) => setSerialInputs((prev) => ({ ...prev, [item.id]: e.target.value }))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddItemSerial(item);
                              }
                            }}
                            inputRef={(el) => {
                              serialInputRefs.current[item.id] = el;
                            }}
                            // readOnly (no disabled) mientras se guarda, para no perder el foco
                            slotProps={{ htmlInput: { readOnly: addingSerialItemId === item.id } }}
                            sx={{ minWidth: 180 }}
                          />
                          <IconButton
                            size="small"
                            color="primary"
                            title="Agregar serie"
                            onClick={() => handleAddItemSerial(item)}
                            disabled={addingSerialItemId === item.id || !(serialInputs[item.id] || '').trim()}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell align="right">{item.quantity}</TableCell>
                    <TableCell align="right">{formatMoney(item.unit_price, order.currency)}</TableCell>
                    <TableCell align="right">{formatMoney(item.subtotal, order.currency)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <Stack sx={{ alignItems: 'flex-end' }} spacing={0.5}>
            <Typography variant="body2">Subtotal: {formatMoney(order.subtotal, order.currency)}</Typography>
            <Typography variant="body2">IGV (18%): {formatMoney(order.igv, order.currency)}</Typography>
            <Typography variant="h6">Total: {formatMoney(order.total, order.currency)}</Typography>
          </Stack>

          <Divider />

          <TextField
            select
            label="Estado del pedido"
            value={statusId}
            onChange={handleStatusChange}
            disabled={savingStatus}
            helperText="Cancelado (pagado) descuenta el stock; Anulado lo devuelve con sus series."
            sx={{ maxWidth: 360 }}
          >
            {statuses.map((status) => (
              <MenuItem key={status.id} value={status.id}>
                {orderStatusLabel(status.name)}
              </MenuItem>
            ))}
          </TextField>

          <Divider />

          <Stack spacing={2}>
            {hasActiveInvoice ? (
              <Stack spacing={2}>
                <Alert severity={INVOICE_STATUS_META[invoice.status]?.severity || 'info'} icon={false}>
                  <Stack direction="row" spacing={1} sx={{ mb: invoice.sunat_response_description ? 1 : 0 }}>
                    <Chip label={invoice.type === 'factura' ? 'Factura' : 'Boleta'} color="primary" size="small" />
                    <Chip
                      label={INVOICE_STATUS_META[invoice.status]?.label || invoice.status}
                      color={INVOICE_STATUS_META[invoice.status]?.color || 'default'}
                      size="small"
                    />
                  </Stack>
                  {invoice.full_number} · emitida el {invoice.issue_date}
                  {invoice.sunat_response_description && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {invoice.sunat_response_description}
                    </Typography>
                  )}
                </Alert>

                {canResend && (
                  <Button
                    variant="contained"
                    startIcon={<ReplayIcon />}
                    onClick={handleResend}
                    disabled={resending}
                  >
                    {resending ? 'Reintentando...' : 'Reintentar envío a SUNAT'}
                  </Button>
                )}

                {isAccepted && (
                  <>
                    <Stack direction="row" spacing={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Enviar comprobante a"
                        value={emailTo}
                        onChange={(e) => setEmailTo(e.target.value)}
                      />
                      <Button variant="contained" startIcon={<SendIcon />} onClick={handleSendEmail} disabled={sending}>
                        {sending ? 'Enviando...' : 'Enviar'}
                      </Button>
                    </Stack>
                    <Stack sx={{ flexWrap: 'wrap' }} direction="row" spacing={2} useFlexGap>
                      <Button
                        variant="contained"
                        startIcon={<PrintIcon />}
                        onClick={() =>
                          handlePrintTicket(
                            'invoice',
                            () => ordersApi.downloadInvoicePdf(order.id, 'ticket'),
                            'No se pudo imprimir el comprobante.'
                          )
                        }
                        disabled={printingTicket === 'invoice'}
                      >
                        {printingTicket === 'invoice' ? 'Generando...' : 'Imprimir ticket'}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownload('a4')}
                        disabled={downloadingPdf}
                      >
                        {downloadingPdf ? 'Generando...' : 'Descargar PDF'}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<PointOfSaleIcon />}
                        onClick={() => handleDownload('ticket')}
                        disabled={downloadingTicket}
                      >
                        {downloadingTicket ? 'Generando...' : 'Descargar ticket'}
                      </Button>
                    </Stack>
                  </>
                )}

                <Button color="error" onClick={() => setVoidDialogOpen(true)} sx={{ alignSelf: 'flex-start' }}>
                  Anular comprobante
                </Button>
              </Stack>
            ) : (
              <Stack spacing={2}>
                {invoice?.status === 'anulada' && (
                  <Alert severity="warning">
                    El comprobante {invoice.full_number} fue anulado
                    {invoice.credit_note?.status === 'aceptada' || invoice.credit_note?.status === 'observada'
                      ? ` ante SUNAT con la nota de crédito ${invoice.credit_note.full_number}`
                      : ' (solo en el sistema)'}
                    . Puedes emitir uno nuevo.
                  </Alert>
                )}
                {invoice?.status === 'anulada' && invoice.void_reason_code && (
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      Sustento: {invoice.void_reason_label}
                      {invoice.void_observation && ` · ${invoice.void_observation}`}
                    </Typography>
                    <Stack sx={{ flexWrap: 'wrap' }} direction="row" spacing={2} useFlexGap>
                      <Button
                        variant="outlined"
                        startIcon={<ReceiptIcon />}
                        onClick={handleDownloadCreditNote}
                        disabled={downloadingCreditNote}
                      >
                        {downloadingCreditNote ? 'Generando...' : 'Descargar nota de crédito'}
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<PrintIcon />}
                        onClick={() =>
                          handlePrintTicket(
                            'credit-note',
                            () => ordersApi.downloadCreditNotePdf(order.id, 'ticket'),
                            'No se pudo imprimir la nota de crédito.'
                          )
                        }
                        disabled={printingTicket === 'credit-note'}
                      >
                        {printingTicket === 'credit-note' ? 'Generando...' : 'Imprimir ticket'}
                      </Button>
                    </Stack>
                  </Stack>
                )}
                {hasMissingSerials && (
                  <Alert severity="warning">
                    Antes de emitir el comprobante o descargar la nota de venta, completa la serie de cada unidad en
                    la tabla de arriba (a mano o con una lectora de códigos de barras).
                  </Alert>
                )}
                <Stack direction="row" spacing={2}>
                  <TextField
                    select
                    label="Tipo de documento"
                    size="small"
                    value={documentType}
                    onChange={(e) => setDocumentType(e.target.value)}
                    sx={{ minWidth: 170 }}
                  >
                    <MenuItem value="DNI">DNI</MenuItem>
                    <MenuItem value="CE">Carné de extranjería</MenuItem>
                    <MenuItem value="PASAPORTE">Pasaporte</MenuItem>
                    <MenuItem value="RUC">RUC</MenuItem>
                  </TextField>
                  <TextField
                    fullWidth
                    label="N.° de documento"
                    size="small"
                    value={documentNumber}
                    onChange={(e) => setDocumentNumber(e.target.value)}
                  />
                  {canLookupDocument && (
                    <Button
                      variant="outlined"
                      startIcon={<SearchIcon />}
                      onClick={handleLookupDocument}
                      disabled={lookingUpDocument}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {lookingUpDocument ? 'Buscando...' : 'Buscar'}
                    </Button>
                  )}
                </Stack>
                {documentType === 'CE' || documentType === 'PASAPORTE' ? (
                  // Sin busqueda automatica (Decolecta no cubre C.E. ni pasaporte): el nombre
                  // se escribe a mano.
                  <TextField
                    label="Nombre completo"
                    size="small"
                    value={documentName}
                    onChange={(e) => setDocumentName(e.target.value)}
                  />
                ) : (
                  documentName && (
                    <Typography variant="body2">
                      {documentType === 'RUC' ? 'Razón social' : 'Nombre del cliente'}: {documentName}
                    </Typography>
                  )
                )}
                {invoiceTypeToIssue === 'factura' &&
                  (documentAddress ? (
                    <Typography variant="body2">Dirección: {documentAddress}</Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      Sin dirección (se obtiene al buscar el RUC arriba; obligatoria ante SUNAT para factura).
                    </Typography>
                  ))}
                {missingDocumentFields && (
                  <Alert severity="warning">
                    {invoiceTypeToIssue === 'factura'
                      ? 'Completa RUC, razón social y dirección (busca el RUC arriba) antes de emitir la factura.'
                      : 'Ingresa el nombre del cliente para el documento indicado, o deja el N.° de documento en blanco para una venta de mostrador.'}
                  </Alert>
                )}
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => handleIssue(invoiceTypeToIssue)}
                  disabled={issuing || hasMissingSerials || missingDocumentFields}
                >
                  {issuing ? 'Emitiendo...' : `Emitir ${invoiceTypeToIssue}`}
                </Button>
              </Stack>
            )}

            {/* Nota de venta: solo mientras el pedido no tenga un comprobante de SUNAT activo
                (sin emitir todavia o con el comprobante anulado). */}
            {!hasActiveInvoice && (
              <>
                <Divider>o</Divider>
                <TextField
                  label="Nombre del cliente (nota de venta)"
                  placeholder="Ej. Juan Pérez"
                  value={noteName}
                  onChange={(e) => setNoteName(e.target.value)}
                  helperText="Solo se imprime el nombre, sin DNI."
                  slotProps={{ htmlInput: { maxLength: 255 } }}
                />
                {/* Mismos formatos que la boleta/factura: ticket en la tiketera, PDF A4 y ticket en PDF. */}
                <Stack sx={{ flexWrap: 'wrap', alignItems: 'center' }} direction="row" spacing={2} useFlexGap>
                  <Typography variant="subtitle2" sx={{ width: '100%' }}>
                    Nota de venta
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<PrintIcon />}
                    onClick={() =>
                      handlePrintTicket(
                        'nota-venta',
                        async () => {
                          await saveNoteName();
                          return ordersApi.downloadNotaVentaPdf(order.id, 'ticket');
                        },
                        'No se pudo imprimir la nota de venta.'
                      )
                    }
                    disabled={printingTicket === 'nota-venta' || hasMissingSerials}
                  >
                    {printingTicket === 'nota-venta' ? 'Generando...' : 'Imprimir ticket'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownloadNotaVenta('a4')}
                    disabled={Boolean(downloadingNota) || hasMissingSerials}
                  >
                    {downloadingNota === 'a4' ? 'Generando...' : 'Descargar PDF'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<PointOfSaleIcon />}
                    onClick={() => handleDownloadNotaVenta('ticket')}
                    disabled={Boolean(downloadingNota) || hasMissingSerials}
                  >
                    {downloadingNota === 'ticket' ? 'Generando...' : 'Descargar ticket'}
                  </Button>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  La nota de venta es un documento interno sin validez tributaria (no pasa por SUNAT ni consume el
                  correlativo), útil cuando el cliente no necesita boleta o factura.
                </Typography>
              </>
            )}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>

    <VoidInvoiceDialog
      open={voidDialogOpen}
      invoice={invoice}
      voiding={voiding}
      onClose={() => setVoidDialogOpen(false)}
      onConfirm={handleVoid}
    />
    </>
  );
}

OrderDetailDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  order: PropTypes.object,
  statuses: PropTypes.array.isRequired,
  onChanged: PropTypes.func.isRequired,
};

OrderDetailDialog.defaultProps = {
  order: null,
};
